import { Pencil } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { DetailField, DetailValue } from "./details";
import { changedValues, displayValue, toInput } from "./details";

interface GraphDetailsProps {
  title: string;
  fields: DetailField[];
  /** Saves `{ key: value | null }` for the fields that changed. */
  onSave: (changes: Record<string, DetailValue>) => Promise<unknown>;
  isSaving: boolean;
  /** False hides Edit (the user's role cannot change these, W9 F4). */
  canEdit?: boolean;
}

const SELECT_CLASS =
  "border-input h-9 w-full rounded-md border bg-transparent px-3 text-sm shadow-xs";

/**
 * A record's facts as a label / value list, with one Edit form for all of
 * them (W8 D5): the deal's mortgage facts and the tenant's custom fields.
 */
export function GraphDetails({
  title,
  fields,
  onSave,
  isSaving,
  canEdit = true,
}: GraphDetailsProps) {
  const [draft, setDraft] = useState<Record<string, string> | null>(null);

  const startEditing = () =>
    setDraft(Object.fromEntries(fields.map((f) => [f.key, toInput(f.value)])));

  const save = async () => {
    if (!draft) return;
    const changes = changedValues(fields, draft);
    if (Object.keys(changes).length) await onSave(changes);
    setDraft(null);
  };

  return (
    <section aria-label={title} className="@container">
      <div className="mb-2 flex items-center gap-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {draft || !canEdit ? null : (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto h-7 text-muted-foreground"
            onClick={startEditing}
          >
            <Pencil className="size-3.5" />
            Edit
          </Button>
        )}
      </div>
      {draft ? (
        <form
          className="grid max-w-xl gap-3 @md:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            save().catch(() => undefined);
          }}
        >
          {fields.map((field) => (
            <FieldInput
              key={field.key}
              field={field}
              value={draft[field.key] ?? ""}
              onChange={(value) => setDraft({ ...draft, [field.key]: value })}
            />
          ))}
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" size="sm" disabled={isSaving}>
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setDraft(null)}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <dl className="grid max-w-xl grid-cols-[minmax(0,8rem)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm @md:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]">
          {fields.map((field) => (
            <div key={field.key} className="contents">
              <dt className="text-muted-foreground">{field.label}</dt>
              <dd>{displayValue(field)}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: DetailField;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = `detail-${field.key}`;
  const options =
    field.type === "boolean"
      ? [
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ]
      : field.choices;
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{field.label}</Label>
      {options ? (
        <select
          id={id}
          className={SELECT_CLASS}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">—</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <Input
          id={id}
          type={
            field.type === "number"
              ? "number"
              : field.type === "date"
                ? "date"
                : "text"
          }
          step={field.type === "number" ? "any" : undefined}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}
